/* eslint-disable no-shadow */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Avatar,
  Stack,
} from '@mui/material';
import { Menu as MenuIcon, Close } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { bgBlur } from 'src/theme/css';
import { useOffSetTop } from 'src/hooks/use-off-set-top';

import Iconify from 'src/components/iconify';
import AppVersion from 'src/components/AppVersion';
import Scrollbar from 'src/components/scrollbar';

import ScrollAnimation from './utils/ScrollAnimation';
import LandingPage from './screens/LandingPage';

const LANDING_HEADER_HEIGHT = { xs: '64px', md: '64px' };

const Navbar = ({ activeSection, onNavigateToSection, isMinimal }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();

  const offsetTop = useOffSetTop(60);

  const handleGetStarted = () => {
    navigate('/onboard', {
      state: {
        selectedPlan: null,
        isTrial: false,
      },
    });
  };

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'Features', id: 'features' },
    { label: 'Pricing', id: 'pricing' },
  ];

  const handleNav = (id) => {
    if (onNavigateToSection) {
      onNavigateToSection(id);
    }

    setMobileOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'background.default',
          backgroundImage: 'none',

          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',

          borderBottom: 1,
          borderColor: 'divider',

          boxShadow: theme.customShadows?.z8,

          transition: theme.transitions.create(['backdrop-filter', 'height'], {
            duration: theme.transitions.duration.shorter,
          }),

          ...(offsetTop && {
            ...bgBlur({
              color: theme.palette.background.default,
            }),

            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
          }),
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              justifyContent: 'space-between',
              px: { xs: 1, sm: 0 },

              height: {
                xs: 64,
                md: 63, // was 70
              },
              minHeight: {
                xs: '64px !important',
                md: '63px !important',
              },

              transition: theme.transitions.create(['height'], {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.shorter,
              }),
            }}
          >
            {/* LOGO */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ cursor: 'pointer' }}
              onClick={() => handleNav('home')}
            >
              <Box
                component="img"
                src="/assets/MeruDesk.png"
                alt="MeruDesk"
                sx={{
                  width: 162, // was 180
                  height: 'auto',
                  display: 'block',
                  transition: 'width 0.3s ease',
                }}
              />
            </Stack>

            {/* MINIMAL MODE */}
            {isMinimal && (
              <Button
                onClick={() => navigate('/')}
                startIcon={<Iconify icon="solar:home-2-bold-duotone" width={18} />}
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  fontWeight: 700,
                  px: 1.2,
                  py: 0.5,
                  borderRadius: 1,
                  color: 'common.white',
                  bgcolor: 'primary.main',
                  boxShadow: (theme) => theme.customShadows?.primary,
                  transition: 'all 0.3s',

                  '&:hover': {
                    bgcolor: 'primary.dark',
                    transform: 'translateY(-1px)',
                    boxShadow: (theme) => theme.customShadows?.primary,
                  },
                }}
              >
                Back to Home
              </Button>
            )}

            {/* FULL NAV */}
            {!isMinimal && (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2.5} // was 3
                  sx={{
                    display: {
                      xs: 'none',
                      md: 'flex',
                    },
                  }}
                >
                  {navItems.map((item) => (
                    <Typography
                      key={item.id}
                      variant="body2"
                      onClick={() => handleNav(item.id)}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.8rem', // was 0.85rem

                        color: activeSection === item.id ? 'primary.main' : 'text.secondary',

                        cursor: 'pointer',
                        position: 'relative',

                        transition: 'color 0.2s ease',

                        '&:hover': {
                          color: 'primary.main',
                        },

                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: -4,
                          left: 0,

                          width: activeSection === item.id ? '100%' : '0%',

                          height: '2px',

                          bgcolor: 'primary.main',

                          transition: 'width 0.3s ease',
                        },
                      }}
                    >
                      {item.label}
                    </Typography>
                  ))}

                  <Stack direction="row" spacing={1} ml={2}>
                    <Button
                      size="small"
                      sx={{
                        color: 'text.primary',

                        '&:hover': {
                          bgcolor: 'transparent',
                          color: 'primary.main',
                        },
                      }}
                      onClick={() => navigate('/find-workspace')}
                    >
                      Login
                    </Button>

                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={handleGetStarted}
                    >
                      Get Started
                    </Button>
                  </Stack>
                </Stack>

                {/* MOBILE MENU */}
                <IconButton
                  sx={{
                    display: {
                      xs: 'flex',
                      md: 'none',
                    },
                    width: 44,
                    height: 44,
                    color: 'text.secondary',
                  }}
                  onClick={() => setMobileOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer anchor="top" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700}>
              Menu
            </Typography>

            <IconButton onClick={() => setMobileOpen(false)}>
              <Close />
            </IconButton>
          </Stack>

          <List>
            {navItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton onClick={() => handleNav(item.id)}>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: 600,

                      color: activeSection === item.id ? 'primary.main' : 'text.secondary',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <Button
              fullWidth
              variant="outlined"
              sx={{
                color: 'text.primary',
                borderColor: 'divider',
              }}
              onClick={() => {
                navigate('/find-workspace');
                setMobileOpen(false);
              }}
            >
              Login
            </Button>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => {
                handleGetStarted();
                setMobileOpen(false);
              }}
            >
              Get Started
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};

const CTA = ({ onNavigateToSection }) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        position: 'relative',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          bgcolor: 'rgba(32,101,209,0.1)',
          borderRadius: '50%',
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -100,
          left: -100,
          width: 300,
          height: 300,
          bgcolor: 'rgba(118,53,220,0.1)',
          borderRadius: '50%',
          filter: 'blur(60px)',
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
        <ScrollAnimation direction="up">
          <Typography
            variant="h3"
            color="text.primary"
            mb={3}
            sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, letterSpacing: '-1px' }}
          >
            Ready to upgrade your hiring stack?
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" mb={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ px: 4 }}
              onClick={() => onNavigateToSection('pricing')}
            >
              View Plans
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                color: 'text.primary',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'rgba(145,158,171,0.04)', borderColor: 'divider' },
              }}
              onClick={() => navigate('/book-demo')}
            >
              Book Demo
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            No credit card required. 14-day free trial on Premium.
          </Typography>
        </ScrollAnimation>
      </Container>
    </Box>
  );
};

const Footer = ({ onNavigateToSection }) => {
  const navigate = useNavigate();
  return (
    <Box
      component="footer"
      sx={{ bgcolor: 'background.paper', pt: 6, pb: 4, borderTop: 1, borderColor: 'divider' }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flexWrap: 'wrap',
            gap: 4,
            mb: 4,
          }}
        >
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 35%' } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              mb={1.5}
              sx={{ cursor: 'pointer' }}
              onClick={() => onNavigateToSection('home')}
            >
              <Box
                component="img"
                src="/assets/MeruDesk.png"
                alt="MeruDesk"
                sx={{
                  width: 200,
                  height: 'auto',
                  // objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ maxWidth: 260, mb: 2, display: 'block', lineHeight: 1.6 }}
            >
              The complete ATS solution for modern talent acquisition. Source, hire, and onboard
              with unprecedented efficiency.
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minWidth: 100 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.primary"
              mb={1}
              display="block"
              sx={{ textTransform: 'uppercase' }}
            >
              Product
            </Typography>
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                onClick={() => onNavigateToSection('features')}
              >
                Features
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                onClick={() => onNavigateToSection('pricing')}
              >
                Pricing
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minWidth: 100 }}>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.primary"
              mb={1}
              display="block"
              sx={{ textTransform: 'uppercase' }}
            >
              Company
            </Typography>
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                onClick={() =>
                navigate(`/privacy-policy?returnUrl=${encodeURIComponent(
                    window.location.href
                  )}`)
                }
                sx={{
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                Privacy Policy
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                onClick={() =>
                  navigate(`/terms-of-service?returnUrl=${encodeURIComponent(
                    window.location.href
                  )}`)
                }
                sx={{
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                Terms
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                onClick={() =>
                  navigate(`/ai-terms?returnUrl=${encodeURIComponent(
                    window.location.href
                  )}`)
                }
                sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
              >
                AI Terms
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            pt: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
          }}
        >
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} CyberMeru Technologies Pvt. Ltd. All rights reserved.
            </Typography>
            <AppVersion size="small" variant="footer" showLabel />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography variant="caption" color="text.secondary">
              English (IN)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              INR (₹)
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export const Layout = ({
  children,
  activeSection,
  onNavigateToSection,
  scrollContainerRef,

  isMinimal,
  hideCTA,
}) => (
  <Box sx={{ height: '100dvh', overflow: 'hidden' }}>
    <Navbar
      activeSection={activeSection}
      onNavigateToSection={onNavigateToSection}
      isMinimal={isMinimal}
    />
    <Scrollbar
      ref={scrollContainerRef}
      sx={{
        mt: LANDING_HEADER_HEIGHT,
        height: {
          xs: `calc(100dvh - ${LANDING_HEADER_HEIGHT.xs})`,
          md: `calc(100dvh - ${LANDING_HEADER_HEIGHT.md})`,
        },
        '& .simplebar-content': {
          minHeight: '100%',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          mt: { xs: '-64px', md: '-64px' },
        }}
      >
        <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
        {!hideCTA && <CTA onNavigateToSection={onNavigateToSection} />}
        <Footer onNavigateToSection={onNavigateToSection} />
      </Box>
    </Scrollbar>
  </Box>
);
const LandingLayout = () => {
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'features', 'pricing'];
      const navOffset = 64;

      const current = sections.reduce((active, section) => {
        const el = document.getElementById(section);
        if (!el) return active;

        const rect = el.getBoundingClientRect();
        return rect.top <= navOffset + 90 ? section : active;
      }, 'home');

      setActiveSection(current);
    };

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return undefined;

    scrollContainer.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    const scrollContainer = scrollContainerRef.current;
    if (element && scrollContainer) {
      const offset = 64;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + scrollContainer.scrollTop - offset;
      scrollContainer.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        scrollToSection(location.state.scrollTo);
      }, 100);
    }
  }, [location]);

  const handleSelectPlan = (plan) => {
    // Replaced registration redirection with a simple alert or scroll for the standalone landing page
    console.log('Plan selected:', plan.name);
  };
  return (
    <Layout
      activeSection={activeSection}
      onNavigateToSection={scrollToSection}
      scrollContainerRef={scrollContainerRef}
      isMinimal={false}
      hideCTA={false}
    >
      <LandingPage
        onStartFreeTrial={() => scrollToSection('pricing')}
        onNavigateToSection={scrollToSection}
        onSelectPlan={handleSelectPlan}
      />
    </Layout>
  );
};

export default LandingLayout;
